import { Link } from "react-router-dom";
import logo from "@/assets/logo-mente-sa.png";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { useTranslation } from "react-i18next";
import { MessageCircle, ShoppingBag, Instagram, BookOpen } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

const SocialIcon = ({ type, size = 16 }: { type: string; size?: number }) => {
  switch (type) {
    case "instagram": return <Instagram size={size} />;
    case "facebook": return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>;
    case "tiktok": return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.97a8.35 8.35 0 004.76 1.49V7.01a4.79 4.79 0 01-1-.32z"/></svg>;
    case "youtube": return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.43z"/><polygon points="9.75,15.02 15.5,11.75 9.75,8.48" fill="white"/></svg>;
    case "linkedin": return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>;
    case "twitter": return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    case "pinterest": return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12 0a12 12 0 00-4.37 23.17c-.1-.94-.2-2.38.04-3.41l1.44-6.13s-.37-.74-.37-1.82c0-1.71.99-2.98 2.22-2.98 1.05 0 1.55.78 1.55 1.72 0 1.05-.67 2.62-1.01 4.07-.29 1.2.6 2.18 1.79 2.18 2.14 0 3.79-2.26 3.79-5.52 0-2.89-2.08-4.9-5.04-4.9-3.43 0-5.45 2.58-5.45 5.24 0 1.04.4 2.15.9 2.75a.36.36 0 01.08.35l-.34 1.36c-.05.22-.18.27-.41.16-1.54-.72-2.5-2.96-2.5-4.77 0-3.88 2.82-7.44 8.13-7.44 4.27 0 7.59 3.04 7.59 7.11 0 4.24-2.67 7.65-6.39 7.65-1.25 0-2.42-.65-2.82-1.42l-.77 2.93c-.28 1.07-1.04 2.42-1.55 3.24A12 12 0 1012 0z"/></svg>;
    case "website": return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>;
    default: return null;
  }
};

const Footer = () => {
  const { c, content } = useLocalizedContent();
  const { t } = useTranslation();
  const businessName = content.business_name || "Mente Sã";
  const footerText = c("footer_text", "footer.text", "Bem-estar ao domicílio");
  const footerCopyright = c("footer_copyright", "footer.copyright", "Todos os direitos reservados.");
  const logoUrl = content.logo_url || logo;
  const whatsappRaw = content.whatsapp_number || "351915883513";
  const whatsappDigits = whatsappRaw.replace(/\D/g, "");
  const whatsappUrl = `https://wa.me/${whatsappDigits || "351915883513"}`;

  const socialLinks = [
    { key: "instagram", url: content.instagram_url, label: "Instagram" },
    { key: "facebook", url: content.facebook_url, label: "Facebook" },
    { key: "tiktok", url: content.tiktok_url, label: "TikTok" },
    { key: "youtube", url: content.youtube_url, label: "YouTube" },
    { key: "linkedin", url: content.linkedin_url, label: "LinkedIn" },
    { key: "twitter", url: content.twitter_url, label: "X (Twitter)" },
    { key: "pinterest", url: content.pinterest_url, label: "Pinterest" },
    { key: "website", url: content.website_url, label: "Website" },
  ].filter((s) => !!s.url);

  return (
    <footer className="py-10 border-t border-border">
      <div className="container mx-auto px-6">
        <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt={businessName} className="h-10 w-10 rounded-full object-cover" width={40} height={40} loading="lazy" />
            <span className="font-display text-lg font-bold text-primary">{businessName}</span>
          </div>

          <nav className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">{t("nav.home")}</Link>
            <Link to="/servicos" className="hover:text-primary transition-colors">{t("nav.services")}</Link>
            <Link to="/#sobre" className="hover:text-primary transition-colors">{t("nav.about")}</Link>
            <Link to="/#testemunhos" className="hover:text-primary transition-colors">{t("nav.testimonials")}</Link>
            <Link to="/#contacto" className="hover:text-primary transition-colors">{t("nav.contact")}</Link>
            <Link to="/loja" className="hover:text-primary transition-colors inline-flex items-center gap-1">
              <ShoppingBag size={14} /> {t("nav.shop")}
            </Link>
          </nav>

          <div className="flex items-center gap-3 flex-wrap justify-center">
            {socialLinks.map((social) => (
              <a
                key={social.key}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
                aria-label={social.label}
              >
                <SocialIcon type={social.key} />
              </a>
            ))}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-5 py-2 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-border text-center space-y-3">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {businessName} — {footerText}. {footerCopyright}
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/politica-privacidade" className="text-xs text-primary hover:underline">
              {t("footer.privacyPolicy")}
            </Link>
            <a
              href="https://www.livroreclamacoes.pt/Inicio/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <BookOpen size={12} />
              {t("footer.complaintsBook")}
            </a>
            <LanguageSelector />
          </div>
          <div className="flex items-center justify-center gap-2 pt-2">
            <span className="text-[10px] text-muted-foreground">{t("footer.developedBy")}</span>
            <a
              href="https://criahub.global/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center hover:opacity-75 transition-opacity"
            >
              <img
                src="/images/logo-criahub.svg"
                alt="CriaHub"
                className="h-5"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
